import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { useRestaurant } from "@/lib/restaurantContext";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Store, CalendarClock, ExternalLink, AlertCircle, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function RestaurantsList() {
  const { restaurants, refresh, setActive } = useRestaurant();
  const [sub, setSub] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [isEvent, setIsEvent] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadSub = async () => {
    try { const { data } = await api.get("/subscription"); setSub(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadSub(); }, []);

  const create = async () => {
    if (!name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const { data } = await api.post("/restaurants", { name: name.trim(), is_event: isEvent });
      toast.success(`Created "${data.name}"`);
      setOpen(false); setName(""); setIsEvent(false);
      await refresh();
      setActive(data.id);
      loadSub();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Permanently delete "${r.name}" and all its menu data?`)) return;
    try {
      await api.delete(`/restaurants/${r.id}`);
      toast.success("Outlet deleted");
      await refresh(); loadSub();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rename = async (r) => {
    const fresh = window.prompt(`Rename "${r.name}" to:`, r.name);
    if (fresh == null) return;
    const trimmed = fresh.trim();
    if (!trimmed || trimmed === r.name) return;
    try {
      setActive(r.id); // ensure X-Restaurant-Id targets the right outlet
      await api.patch("/restaurant", { name: trimmed });
      toast.success("Outlet renamed");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const subscription = sub?.subscription;
  const outletCount = sub?.outlet_count || 0;
  const unlimited = !!subscription?.unlimited;
  const limit = subscription && !subscription.is_event_pack ? subscription.outlet_limit : 0;
  const canCreateRestaurant = !!subscription && !subscription.is_event_pack && (unlimited || outletCount < limit);
  const canCreateEvent = !!subscription && subscription.is_event_pack;

  return (
    <DashboardLayout role="owner">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange mb-2">Outlets</div>
          <h1 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight">Manage your outlets</h1>
          <p className="text-foreground/60 mt-2 text-sm">
            {subscription ? (
              <>Plan: <strong>{subscription.plan}</strong> · Using <strong>{outletCount}{unlimited ? " / ∞" : limit > 0 ? `/${limit}` : ""}</strong> outlet{outletCount === 1 ? "" : "s"}</>
            ) : (
              <>No active plan. <Link to="/dashboard/billing" className="brand-orange font-semibold hover:underline">Pick a plan</Link> to create outlets.</>
            )}
          </p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-brand-orange hover:bg-[#E65C00] text-white rounded-full" data-testid="add-outlet-btn">
          <Plus className="w-4 h-4" /> Add outlet
        </Button>
      </div>

      {subscription && !subscription.is_event_pack && !unlimited && outletCount >= limit && (
        <Card className="mb-6 p-4 border border-amber-500/30 bg-amber-500/5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            You&rsquo;ve hit the limit for your current plan. <Link to="/dashboard/billing" className="brand-orange font-semibold hover:underline">Upgrade</Link> to add more outlets.
          </div>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        {restaurants.map((r) => (
          <Card key={r.id} className="p-6 border border-border" data-testid={`outlet-${r.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${r.is_event ? "bg-brand-orange/10" : "bg-brand-navy/10"}`}>
                  {r.is_event ? <CalendarClock className="w-5 h-5 brand-orange" /> : <Store className="w-5 h-5 brand-navy" />}
                </div>
                <div>
                  <div className="font-heading font-semibold text-lg">{r.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">/m/{r.slug}</div>
                </div>
              </div>
              {r.is_event && <Badge className="bg-brand-orange hover:bg-brand-orange">Event</Badge>}
            </div>
            {r.is_event && r.expires_at && (
              <div className="text-xs text-muted-foreground mb-3">
                Expires {new Date(r.expires_at).toLocaleDateString()}
              </div>
            )}
            <div className="flex gap-2 mt-3 flex-wrap">
              <Button size="sm" onClick={() => setActive(r.id)} className="bg-brand-navy hover:bg-[#1A2B42] text-white" data-testid={`activate-${r.id}`}>Manage menu</Button>
              <Button size="sm" variant="outline" onClick={() => rename(r)} data-testid={`rename-${r.id}`}>
                <Pencil className="w-3.5 h-3.5" /> Rename
              </Button>
              <a href={`/m/${r.slug}`} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" data-testid={`view-${r.id}`}><ExternalLink className="w-3.5 h-3.5" /> View</Button>
              </a>
              <Button size="sm" variant="ghost" onClick={() => remove(r)} className="ml-auto text-destructive hover:text-destructive" data-testid={`del-${r.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        ))}
        {restaurants.length === 0 && (
          <Card className="p-10 text-center border-dashed">
            <Store className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <div className="font-heading font-semibold text-lg">No outlets yet</div>
            <p className="text-sm text-foreground/60 mt-1">
              {subscription ? "Click \"Add outlet\" to create your first menu." : "Subscribe to a plan to add your first outlet."}
            </p>
            {!subscription && <Link to="/dashboard/billing"><Button className="mt-4 bg-brand-orange text-white rounded-full">Choose a plan</Button></Link>}
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-outlet-dialog">
          <DialogHeader>
            <DialogTitle>Add a new outlet</DialogTitle>
            <DialogDescription>Each outlet gets its own menu and unique QR code.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Outlet / restaurant name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="new-outlet-name" />
            </div>
            <label className="flex items-center gap-3 text-sm">
              <Switch checked={isEvent} onCheckedChange={setIsEvent} data-testid="event-mode-switch" />
              <span>This is a one-off Event/Party menu (10-day validity)</span>
            </label>
            {isEvent && !canCreateEvent && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-3 rounded-md">
                You need an active Event/Party plan to create event menus.
              </div>
            )}
            {!isEvent && !canCreateRestaurant && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-3 rounded-md">
                {!subscription ? "No active plan." : "Outlet limit reached on your current plan."} <Link to="/dashboard/billing" className="font-semibold underline">Choose a plan →</Link>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={busy || (isEvent ? !canCreateEvent : !canCreateRestaurant)} className="bg-brand-orange hover:bg-[#E65C00] text-white" data-testid="create-outlet-submit">
              {busy ? "Creating…" : "Create outlet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
